/**
 * The global top bar: where you are, and everything you can reach from it.
 *
 *   [NexERP] [organization ▾] / [app ▾]    [ Search… Ctrl K ]    [+] [bell] [theme] [you]
 *
 * The app switcher opens the launcher; the search opens the command center;
 * the rest are the create menu, notifications, a one-click theme toggle and
 * the account menu. On a small screen it condenses to the brand, the app and
 * a menu button, and the mobile tab bar takes over the rest.
 *
 * ORGANIZATION SWITCHING navigates with the RAW router rather than the
 * organization-scoped shim, and it has to: the destination belongs to the
 * organization being switched TO, so resolving it against the one currently
 * open would land inside the organization we are leaving. Switching remounts
 * the data context (AppLayout keys it on the organization) rather than
 * patching state in place, so no screen is ever half-switched.
 *
 * There is no "new organization" action. Organizations are created by a
 * platform administrator in ERP-ADMIN, together with their owner.
 */
import { Suspense, lazy } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Menu, MenuItem, MenuLabel } from '../components/Menu.jsx';
import { Avatar } from '../components/Layout.jsx';
import { Icon } from '../components/Icon.jsx';
import { Popover } from '../components/Popover.jsx';
import { useAuth } from '../context/AuthContext.jsx';
import { useAppearance } from '../hooks/useAppearance.js';
import { Link, orgBasePath } from '../routes/orgNavigation.jsx';
import { AppIcon, Brand } from './Brand.jsx';
import { appName } from './apps.js';
import { NotificationCenter } from './NotificationCenter.jsx';
import { QuickCreate } from './QuickCreate.jsx';
import { UserMenu } from './UserMenu.jsx';

const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform ?? '');
export const MOD_KEY = isMac ? '⌘' : 'Ctrl';

// Its own chunk, like the command center: prefetched by the shell when idle.
const AppLauncher = lazy(() => import('./AppLauncher.jsx').then((module) => ({ default: module.AppLauncher })));

function LauncherFallback() {
  return (
    <div className="launcher" aria-hidden="true">
      <div className="launcher-search">
        <div className="skeleton" style={{ width: '100%', height: 38, borderRadius: 7 }} />
      </div>
    </div>
  );
}

function OrgSwitcher() {
  const { t } = useTranslation('navigation');
  const { organizations, activeOrganization, tenantId, selectOrganization } = useAuth();
  const navigate = useNavigate();

  if (!organizations.length) return null;

  /**
   * Both halves matter. The URL is what AppLayout reads to decide which
   * organization is open, and `selectOrganization` is what makes it the one
   * `/` returns you to next time.
   */
  const handleSwitch = (organization) => {
    if (organization.id === tenantId) return;
    selectOrganization(organization.id);
    navigate(orgBasePath(organization.slug), { replace: true });
  };

  return (
    <Menu
      align="left"
      label={t('topbar.switchOrganization')}
      trigger={({ toggle, open }) => (
        <button
          type="button"
          className={`topbar-org ${open ? 'is-open' : ''}`}
          onClick={toggle}
          aria-haspopup="menu"
          aria-expanded={open}
          title={t('topbar.switchOrganization')}
        >
          <Avatar name={activeOrganization?.name} />
          <span className="topbar-org-name truncate">{activeOrganization?.name ?? t('topbar.selectOrganization')}</span>
          <Icon name="chevrons-up-down" size={14} className="subtle" />
        </button>
      )}
    >
      <MenuLabel>{t('topbar.yourOrganizations')}</MenuLabel>
      {organizations.map((organization) => (
        <MenuItem key={organization.id} active={organization.id === tenantId} onClick={() => handleSwitch(organization)}>
          <Avatar name={organization.name} />
          <span className="grow truncate">
            {organization.name}
            {organization.membership?.isOwner && <span className="text-xs subtle"> &middot; {t('topbar.owner')}</span>}
          </span>
          {organization.id === tenantId && <Icon name="check" size={15} />}
        </MenuItem>
      ))}
    </Menu>
  );
}

function ThemeToggle() {
  const { t } = useTranslation('navigation');
  const { resolved, setTheme } = useAppearance();
  const next = resolved === 'dark' ? 'light' : 'dark';
  const label = next === 'dark' ? t('topbar.switchToDark') : t('topbar.switchToLight');
  return (
    <button
      type="button"
      className="topbar-icon-btn topbar-theme"
      onClick={(event) => {
        const rect = event.currentTarget.getBoundingClientRect();
        setTheme(next, { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 });
      }}
      aria-label={label}
      title={label}
    >
      <Icon name={resolved === 'dark' ? 'sun' : 'moon'} size={17} />
    </button>
  );
}

export function Topbar({
  apps,
  currentApp,
  launcherOpen,
  onLauncherOpenChange,
  onOpenCommand,
  onOpenNav,
  createActions,
  pendingApprovals,
  pinned,
  recent,
  loading,
  onShowShortcuts,
}) {
  const { t } = useTranslation('navigation');
  return (
    <header className="topbar">
      <div className="topbar-left">
        {currentApp && (
          <button type="button" className="topbar-icon-btn topbar-menu" onClick={onOpenNav} aria-label={t('contextNav.label', { app: appName(currentApp, t, { bilingual: false }) })}>
            <Icon name="menu" size={18} />
          </button>
        )}

        <Link to="/" className="topbar-brand" aria-label={t('topbar.brandHome')} title={t('items.home.label')}>
          <Brand />
        </Link>

        <span className="topbar-divider" aria-hidden="true" />
        <OrgSwitcher />

        <span className="topbar-slash" aria-hidden="true">
          /
        </span>

        <Popover
          label={t('mobileNav.apps')}
          align="start"
          open={launcherOpen}
          onOpenChange={onLauncherOpenChange}
          panelClassName="popover-launcher"
          initialFocus="panel"
          trigger={({ triggerProps, open }) => (
            <button type="button" className={`topbar-app ${open ? 'is-open' : ''}`} {...triggerProps} title={t('topbar.switchApp')}>
              {currentApp ? <AppIcon app={currentApp} size="sm" /> : <Icon name="apps" size={16} className="subtle" />}
              <span className="topbar-app-name">{currentApp ? appName(currentApp, t) : t('mobileNav.apps')}</span>
              <Icon name="chevron-down" size={14} className="topbar-app-chevron" />
            </button>
          )}
        >
          <Suspense fallback={<LauncherFallback />}>
            <AppLauncher
              apps={apps}
              currentApp={currentApp}
              pinned={pinned}
              recent={recent}
              loading={loading}
              onOpenCommand={onOpenCommand}
            />
          </Suspense>
        </Popover>
      </div>

      <button type="button" className="topbar-search" onClick={onOpenCommand} aria-label={t('topbar.searchNexerp')} aria-keyshortcuts="Control+K Meta+K">
        <Icon name="search" size={15} />
        <span className="topbar-search-text">{t('topbar.searchPlaceholder')}</span>
        <span className="topbar-search-keys" aria-hidden="true">
          <kbd>{MOD_KEY}</kbd>
          <kbd>K</kbd>
        </span>
      </button>

      <div className="topbar-right">
        <button type="button" className="topbar-icon-btn topbar-search-icon" onClick={onOpenCommand} aria-label={t('common:a11y.search')}>
          <Icon name="search" size={17} />
        </button>
        <QuickCreate actions={createActions} currentKey={currentApp?.key} />
        <NotificationCenter pendingApprovals={pendingApprovals} />
        <ThemeToggle />
        <UserMenu onShowShortcuts={onShowShortcuts} />
      </div>
    </header>
  );
}
